/* =====================================================================
   VEREDA · medios y secciones nuevas de la ficha (v5) · sin librerías
   1 Vídeos (mt-video, mt-videos): arrancan con el 35 % a la vista y se pausan al salir; tocarlos los pausa o reanuda;
     con «reducir movimiento» se quedan en el póster con el botón de reproducir; con la pestaña oculta, en pausa.
     preload="none": nada se descarga hasta que hay que reproducir. No dependen de la duración de cada vídeo.
   2 Carrusel de vídeos: puntos (botones) y teclado (flechas, Inicio, Fin). Las flechas son del carril de mt-core.js.
     Las pastillas de cada tarjeta aparecen escalonadas la primera vez que la tarjeta entra en pantalla.
   3 Comparador (mt-antes-despues): asa que se arrastra con el dedo o el ratón + control deslizante con teclado.
   4 Contadores (mt-cifras): cuentan desde 0 la primera vez que se ven; con «reducir movimiento», número hecho.
   5 Cinta (mt-usos en modo cinta): en pausa fuera de pantalla (el ratón encima y el foco la paran con CSS).
   Cada sección carga este archivo: solo arranca la primera copia. En el editor de temas se vuelve a montar al repintar.
   ===================================================================== */
(() => {
  'use strict';
  if (window.mtMedios) return;

  const reducido = window.matchMedia('(prefers-reduced-motion: reduce)');
  const $$ = (sel, raiz = document) => Array.from(raiz.querySelectorAll(sel));
  const hayIO = 'IntersectionObserver' in window;
  const UMBRAL = 0.35;

  /* ---------- 1 · Vídeos ---------- */
  const medios = new Set();

  // ¿Debe estar sonando (sin sonido) este vídeo ahora mismo?
  const quiere = (m) => m.visible && !document.hidden && (m.mano === 'play' || (m.mano !== 'pausa' && !reducido.matches && !m.bloqueado));

  function pintarMedio(m) {
    const enPausa = m.mano === 'pausa' || m.bloqueado || (reducido.matches && m.mano !== 'play');
    m.caja.classList.toggle('es-pausado', enPausa);
    m.boton.setAttribute('aria-label', m.video.paused ? 'Reproducir el vídeo' : 'Pausar el vídeo');
  }

  function aplicar(m) {
    if (quiere(m)) {
      if (m.video.paused) {
        const promesa = m.video.play();
        if (promesa && promesa.catch) {
          promesa.catch((error) => {
            // Una pausa a mitad de arranque no es un fallo; un bloqueo del navegador, sí: se enseña el botón.
            if (error && error.name === 'AbortError') return;
            m.bloqueado = true;
            pintarMedio(m);
          });
        }
      }
    } else if (!m.video.paused) {
      m.video.pause();
    }
    pintarMedio(m);
  }

  const observadorMedios = hayIO
    ? new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            const m = entrada.target.mtMedio;
            if (!m) return;
            m.visible = entrada.isIntersecting && entrada.intersectionRatio >= UMBRAL - 0.01;
            if (m.visible) {
              const celda = m.caja.closest('[data-mt-vids-celda]');
              if (celda) celda.classList.add('es-visible');
            }
            aplicar(m);
          });
        },
        { threshold: [0, UMBRAL, 0.6, 1] }
      )
    : null;

  // 19/10 (auditoría P2-4): el cartel (póster) se pone cuando el vídeo se acerca a la pantalla, no al cargar la
  // página, para que no compita con la foto principal.
  const ponerCartel = (v) => { if (v.dataset.poster && !v.getAttribute('poster')) v.setAttribute('poster', v.dataset.poster); };
  const observadorCarteles = hayIO
    ? new IntersectionObserver((entradas) => {
        entradas.forEach((e) => {
          if (!e.isIntersecting) return;
          ponerCartel(e.target);
          observadorCarteles.unobserve(e.target);
        });
      }, { rootMargin: '900px 0px' })
    : null;

  function montarVideo(caja) {
    if (caja.mtMedio) return;
    const video = caja.querySelector('video[data-mt-video]');
    const boton = caja.querySelector('[data-mt-medio-boton]');
    if (video && video.dataset.poster) {
      if (observadorCarteles) observadorCarteles.observe(video);
      else ponerCartel(video);
    }
    if (!video || !boton) return;
    const m = { caja, video, boton, visible: false, mano: null, bloqueado: false };
    caja.mtMedio = m;
    medios.add(m);
    // Siempre sin sonido, también si el navegador ignora el atributo (necesario para arrancar solos en móvil).
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.addEventListener('play', () => pintarMedio(m));
    video.addEventListener('pause', () => pintarMedio(m));
    boton.addEventListener('click', () => {
      m.bloqueado = false;
      if (video.paused) {
        // Lo pide la persona: se reproduce aunque se vea menos del 35 %.
        m.mano = 'play';
        const promesa = video.play();
        if (promesa && promesa.catch) promesa.catch(() => {});
      } else {
        m.mano = 'pausa';
        video.pause();
      }
      pintarMedio(m);
    });
    if (observadorMedios) observadorMedios.observe(caja);
    else {
      m.visible = true;
      aplicar(m);
    }
    pintarMedio(m);
  }

  document.addEventListener('visibilitychange', () => medios.forEach(aplicar));
  const alCambiarMovimiento = () => {
    medios.forEach(aplicar);
    $$('[data-mt-vids-celda]').forEach((c) => reducido.matches && c.classList.add('es-visible'));
  };
  if (reducido.addEventListener) reducido.addEventListener('change', alCambiarMovimiento);
  else if (reducido.addListener) reducido.addListener(alCambiarMovimiento);

  /* ---------- 2 · Carrusel de vídeos: puntos y teclado ---------- */
  function montarCarrusel(raiz) {
    if (raiz.mtVids) return;
    raiz.mtVids = true;
    const pista = raiz.querySelector('[data-mt-carril-pista]');
    const celdas = $$('[data-mt-vids-celda]', raiz);
    const puntos = $$('[data-mt-vids-ir]', raiz);
    if (!pista || !celdas.length) return;
    if (reducido.matches) celdas.forEach((c) => c.classList.add('es-visible'));

    const relleno = () => parseFloat(getComputedStyle(pista).paddingLeft) || 0;
    const izquierda = (i) => celdas[i].offsetLeft - relleno();
    const maximo = () => pista.scrollWidth - pista.clientWidth;
    const actual = () => {
      if (pista.scrollLeft >= maximo() - 2) return celdas.length - 1;
      let mejor = 0;
      let distancia = Infinity;
      celdas.forEach((c, i) => {
        const d = Math.abs(izquierda(i) - pista.scrollLeft);
        if (d < distancia) {
          distancia = d;
          mejor = i;
        }
      });
      return mejor;
    };
    const ir = (i) => {
      const destino = Math.max(0, Math.min(celdas.length - 1, i));
      pista.scrollTo({ left: Math.min(izquierda(destino), maximo()), behavior: reducido.matches ? 'auto' : 'smooth' });
    };
    const pintarPuntos = () => {
      const i = actual();
      puntos.forEach((p, n) => {
        p.classList.toggle('es-activo', n === i);
        if (n === i) p.setAttribute('aria-current', 'true');
        else p.removeAttribute('aria-current');
      });
    };
    puntos.forEach((p) => p.addEventListener('click', () => ir(parseInt(p.dataset.mtVidsIr, 10))));
    let tic = 0;
    pista.addEventListener(
      'scroll',
      () => {
        if (tic) return;
        tic = requestAnimationFrame(() => {
          tic = 0;
          pintarPuntos();
        });
      },
      { passive: true }
    );
    pista.addEventListener('keydown', (e) => {
      const teclas = { ArrowRight: 1, ArrowLeft: -1 };
      if (e.key in teclas) {
        e.preventDefault();
        ir(actual() + teclas[e.key]);
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        ir(e.key === 'Home' ? 0 : celdas.length - 1);
      }
    });
    window.addEventListener('resize', pintarPuntos, { passive: true });
    pintarPuntos();
    raiz.mtIr = (celda) => {
      const i = celdas.indexOf(celda);
      if (i >= 0) ir(i);
    };
  }

  /* ---------- 3 · Comparador antes / después ---------- */
  function montarComparador(marco) {
    if (marco.mtComparar) return;
    marco.mtComparar = true;
    const rango = marco.querySelector('[data-mt-comparar-rango]');
    const textoAntes = (marco.querySelector('.mt-ad__pastilla--antes') || {}).textContent || 'antes';
    const textoDespues = (marco.querySelector('.mt-ad__pastilla--despues') || {}).textContent || 'después';
    const poner = (valor) => {
      const v = Math.max(0, Math.min(100, valor));
      marco.style.setProperty('--mt-pos', `${v.toFixed(2)}%`);
      if (rango) {
        const r = Math.round(v);
        if (document.activeElement !== rango) rango.value = String(r);
        rango.setAttribute('aria-valuetext', `${r} % ${textoAntes.trim()}, ${100 - r} % ${textoDespues.trim()}`);
      }
    };
    const desde = (e) => {
      const caja = marco.getBoundingClientRect();
      return ((e.clientX - caja.left) / caja.width) * 100;
    };
    let arrastre = null; // { id, tactil, x, y, activo }
    marco.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const tactil = e.pointerType !== 'mouse';
      arrastre = { id: e.pointerId, tactil, x: e.clientX, y: e.clientY, activo: !tactil };
      if (!tactil) {
        e.preventDefault();
        poner(desde(e));
        marco.classList.add('es-arrastrando');
        try { marco.setPointerCapture(e.pointerId); } catch (error) {}
      }
    });
    marco.addEventListener('pointermove', (e) => {
      if (!arrastre || e.pointerId !== arrastre.id) return;
      if (!arrastre.activo) {
        // Con el dedo, solo se arrastra si el gesto es horizontal: el vertical sigue siendo para bajar la página.
        const dx = Math.abs(e.clientX - arrastre.x);
        const dy = Math.abs(e.clientY - arrastre.y);
        if (dx < 6 || dx < dy) return;
        arrastre.activo = true;
        marco.classList.add('es-arrastrando');
        try { marco.setPointerCapture(e.pointerId); } catch (error) {}
      }
      poner(desde(e));
    });
    const soltar = (e) => {
      if (!arrastre || e.pointerId !== arrastre.id) return;
      // Un toque sin arrastrar también coloca el asa.
      if (arrastre.tactil && !arrastre.activo && e.type === 'pointerup') poner(desde(e));
      arrastre = null;
      marco.classList.remove('es-arrastrando');
      try { marco.releasePointerCapture(e.pointerId); } catch (error) {}
    };
    marco.addEventListener('pointerup', soltar);
    marco.addEventListener('pointercancel', soltar);
    marco.addEventListener('dragstart', (e) => e.preventDefault());
    if (rango) rango.addEventListener('input', () => poner(parseFloat(rango.value)));
    poner(rango ? parseFloat(rango.value) : 50);
  }

  /* ---------- 4 · Contadores ---------- */
  function contar(el, final) {
    const duracion = Math.min(1600, 600 + final * 60);
    const inicio = performance.now();
    const paso = (ahora) => {
      const k = Math.min(1, (ahora - inicio) / duracion);
      const suave = 1 - Math.pow(1 - k, 3);
      el.textContent = String(Math.round(final * suave));
      if (k < 1) requestAnimationFrame(paso);
      else el.dataset.mtContado = '1';
    };
    requestAnimationFrame(paso);
  }
  function montarContador(el) {
    if (el.mtContar) return;
    el.mtContar = true;
    const final = parseInt(el.dataset.mtContar, 10);
    if (!Number.isFinite(final) || final <= 0 || reducido.matches || !hayIO) return; // se queda el número hecho
    el.textContent = '0';
    const io = new IntersectionObserver(
      (entradas) => {
        if (!entradas.some((e) => e.isIntersecting)) return;
        io.disconnect();
        contar(el, final);
      },
      { threshold: 0.6 }
    );
    io.observe(el);
  }

  /* ---------- 5 · Cinta de usos: en pausa fuera de pantalla ---------- */
  const observadorCintas = hayIO
    ? new IntersectionObserver((entradas) => entradas.forEach((e) => e.target.classList.toggle('es-pausada', !e.isIntersecting)), { rootMargin: '80px 0px' })
    : null;
  function montarCinta(cinta) {
    if (cinta.mtCinta) return;
    cinta.mtCinta = true;
    if (observadorCintas) observadorCintas.observe(cinta);
  }

  /* ---------- arranque ---------- */
  function iniciar(raiz = document) {
    $$('[data-mt-medio]', raiz).forEach(montarVideo);
    $$('[data-mt-vids]', raiz).forEach(montarCarrusel);
    $$('[data-mt-comparar]', raiz).forEach(montarComparador);
    $$('[data-mt-contar]', raiz).forEach(montarContador);
    $$('[data-mt-cinta]', raiz).forEach(montarCinta);
  }
  window.mtMedios = { iniciar };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => iniciar());
  else iniciar();

  // Editor de temas: al repintar una sección se monta lo nuevo; al quitarla se olvidan sus vídeos.
  document.addEventListener('shopify:section:load', (e) => iniciar(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    medios.forEach((m) => {
      if (!e.target.contains(m.caja)) return;
      if (observadorMedios) observadorMedios.unobserve(m.caja);
      medios.delete(m);
    });
  });
  document.addEventListener('shopify:block:select', (e) => {
    const celda = e.target.closest && e.target.closest('[data-mt-vids-celda]');
    const raiz = celda && celda.closest('[data-mt-vids]');
    if (raiz && raiz.mtIr) raiz.mtIr(celda);
  });
})();
